import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID } from 'services/wp';
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import AlertError from 'components/AlertError';
import validateEmail from 'utils/validateEmail';
import { validatePhone, formatPhoneNumber } from 'utils/validatePhone';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';


const AgentForm = () => {
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        stateLicense: '',
        experienceYears: '',
        currentLicense: '',
        additionalInfo: ''
    });

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = (e) => {
        let { name, value } = e.target;
        if (name === 'phone') {
            value = formatPhoneNumber(value);
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAgreeToTerms = (e) => {
        const { name, type, checked } = e.target;

        if (type === 'checkbox' && name === 'agreeToTerms') {
            setAgreeToTerms(checked);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitted(true);

        const missing = [];
        if (!formData.firstName?.trim()) missing.push('First Name');
        if (!formData.lastName?.trim()) missing.push('Last Name');
        if (!formData.email?.trim()) {
            missing.push('Email');
        } else if (!validateEmail(formData.email)) {
            missing.push('Email (Invalid format)');
        }
        if (!formData.phone?.trim()) {
            missing.push('Phone');
        } else if (!validatePhone(formData.phone)) {
            missing.push('Phone (10 digits)');
        }
        if (!formData.stateLicense?.trim()) missing.push('States Licensed');
        if (!String(formData.experienceYears || '').trim()) missing.push('Years of Experience');
        if (!formData.currentLicense?.trim()) missing.push('Current License');
        if (!formData.additionalInfo?.trim()) missing.push('Additional Info');
        if (!agreeToTerms) missing.push('Terms & Conditions Agreement');

        if (missing.length > 0) {
            return;
        }

        setIsLoading(true);
        setShowErrorMessage(false);

        const jsonData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            stateLicense: formData.stateLicense,
            experienceYears: formData.experienceYears,
            currentLicense: formData.currentLicense,
            additionalInfo: formData.additionalInfo
        };


        try {
            await withSubmissionTimeout(async () => {
                const accountID = await getAccountID();
                const webhookID = await getWebhookID({ formName: "agent" });

                await sendToGHL(jsonData, accountID, webhookID);
            });

            setShowSuccessMessage(true);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 2000);

            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                stateLicense: '',
                experienceYears: '',
                currentLicense: '',
                additionalInfo: ''
            });

            setAgreeToTerms(false);
            setSubmitted(false);

        } catch (error) {
            setErrorMessage(getSubmissionErrorMessage(error));
            setShowErrorMessage(true);
            setAgreeToTerms(false)
        } finally {
            setIsLoading(false);
        }
    };

    const firstNameInvalid = submitted && !formData.firstName?.trim();
    const lastNameInvalid = submitted && !formData.lastName?.trim();
    const emailEmpty = submitted && !formData.email?.trim();
    const emailFormatInvalid = submitted && Boolean(formData.email?.trim()) && !validateEmail(formData.email);
    const emailInvalid = emailEmpty || emailFormatInvalid;
    const phoneEmpty = submitted && !formData.phone?.trim();
    const phoneFormatInvalid = submitted && Boolean(formData.phone?.trim()) && !validatePhone(formData.phone);
    const phoneInvalid = phoneEmpty || phoneFormatInvalid;
    const stateLicenseInvalid = submitted && !formData.stateLicense?.trim();
    const experienceYearsInvalid = submitted && !String(formData.experienceYears || '').trim();
    const currentLicenseInvalid = submitted && !formData.currentLicense?.trim();
    const additionalInfoInvalid = submitted && !formData.additionalInfo?.trim();
    const termsInvalid = submitted && !agreeToTerms;

    return (
        <form className='space-y-4' onSubmit={handleSubmit} noValidate>

            {isLoading && (
                <div className="loader"></div>
            )}

            {showSuccessMessage && (
                <div className="toast toast-center toast-middle z-[9999]">
                    <div className="alert alert-success text-base-100">
                        <span>Message sent successfully.</span>
                    </div>
                </div>
            )}

            <AlertError showError={showErrorMessage} onClose={() => setShowErrorMessage(false)}>
                {errorMessage || 'Something went wrong. Try again later.'}
            </AlertError>

            <div className='md:space-x-4 space-y-4 md:space-y-0 flex flex-col md:flex-row'>
                <div className="w-full relative" data-invalid={firstNameInvalid ? "true" : undefined}>
                    {firstNameInvalid && (
                        <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                            Required
                        </div>
                    )}
                    <input
                        className={`input input-bordered w-full ${firstNameInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        type='text'
                        name='firstName'
                        placeholder='First Name *'
                        value={formData.firstName}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="w-full relative" data-invalid={lastNameInvalid ? "true" : undefined}>
                    {lastNameInvalid && (
                        <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                            Required
                        </div>
                    )}
                    <input
                        className={`input input-bordered w-full ${lastNameInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        type='text'
                        name='lastName'
                        placeholder='Last Name *'
                        value={formData.lastName}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
            <div className='md:space-x-4 space-y-4 md:space-y-0 flex flex-col md:flex-row'>
                <div className="w-full relative" data-invalid={emailInvalid ? "true" : undefined}>
                    {emailInvalid && (
                        <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                            {emailEmpty ? 'Required' : 'Invalid Email'}
                        </div>
                    )}
                    <input
                        className={`input input-bordered w-full ${emailInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        type='email'
                        name='email'
                        placeholder='Email *'
                        value={formData.email}
                        onChange={handleInputChange}
                    />
                </div>
                <div className="w-full relative" data-invalid={phoneInvalid ? "true" : undefined}>
                    {phoneInvalid && (
                        <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                            {phoneEmpty ? 'Required' : 'Please enter a valid 10-digit phone number'}
                        </div>
                    )}
                    <input
                        className={`input input-bordered w-full ${phoneInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                        type='tel'
                        name='phone'
                        placeholder='Phone *'
                        value={formData.phone}
                        onChange={handleInputChange}
                    />
                </div>
            </div>

            <div className='divider md:hidden'></div>

            <div className="w-full relative" data-invalid={stateLicenseInvalid ? "true" : undefined}>
                {stateLicenseInvalid && (
                    <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                        Required
                    </div>
                )}
                <input
                    className={`input input-bordered w-full ${stateLicenseInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                    type='text'
                    name='stateLicense'
                    placeholder='What States do you have License For? *'
                    value={formData.stateLicense}
                    onChange={handleInputChange}
                />
            </div>

            <div className="w-full relative" data-invalid={experienceYearsInvalid ? "true" : undefined}>
                {experienceYearsInvalid && (
                    <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                        Required
                    </div>
                )}
                <input
                    className={`input input-bordered w-full ${experienceYearsInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                    type='number'
                    name='experienceYears'
                    placeholder='Years of Experience in Insurance Sales *'
                    value={formData.experienceYears}
                    onChange={handleInputChange}
                />
            </div>

            <div className="w-full relative" data-invalid={currentLicenseInvalid ? "true" : undefined}>
                {currentLicenseInvalid && (
                    <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                        Required
                    </div>
                )}
                <input
                    className={`input input-bordered w-full ${currentLicenseInvalid ? 'input-error border-2 border-red-600 bg-red-50' : ''}`}
                    type='text'
                    name='currentLicense'
                    placeholder='What License do you currently have *'
                    value={formData.currentLicense}
                    onChange={handleInputChange}
                />
            </div>

            <div className="w-full relative" data-invalid={additionalInfoInvalid ? "true" : undefined}>
                {additionalInfoInvalid && (
                    <div className="absolute bg-red-600 -top-2.5 right-3 text-white text-[10px] font-bold px-2 py-0.5 shadow rounded z-10 pointer-events-none">
                        Required
                    </div>
                )}
                <textarea
                    className={`textarea textarea-bordered w-full ${additionalInfoInvalid ? 'textarea-error border-2 border-red-600 bg-red-50' : ''}`}
                    name='additionalInfo'
                    type="text"
                    placeholder='Anything else to add? *'
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                />
            </div>

            {/* DISCLAIMER */}
            <label
                className={`label cursor-pointer flex items-start rounded-lg p-2 ${termsInvalid ? 'border-2 border-red-600 bg-red-50' : ''}`}
                data-invalid={termsInvalid ? "true" : undefined}
            >
                <input
                    type="checkbox"
                    className={`checkbox rounded mr-4 mt-1 ${termsInvalid ? 'checkbox-error border-red-600' : ''}`}
                    name="agreeToTerms"
                    checked={agreeToTerms}
                    onChange={handleAgreeToTerms}
                />
                <span className="label-text text-xs">
                    {termsInvalid && (
                        <span className="inline-block text-[10px] font-bold text-white bg-red-600 px-1.5 py-0.5 rounded mr-2 mb-1">
                            Required: Please check to agree
                        </span>
                    )}
                    By providing your email address and phone number, you agree to receive email and text messages from
                    Insurance Enterprise USA about our products and services.
                    Email or text HELP to
                    <span className='underline text-blue-600 hover:text-blue-400 px-1'>
                        <a href='mailto:benefits@insuranceenterpriseusa.com' rel='noreferrer'>
                            benefits@insuranceenterpriseusa.com
                        </a>
                    </span>

                    or
                    <a href='tel:8883506605' rel='noreferrer' className='underline text-blue-600 hover:text-blue-400 px-1'>
                        888-350-6605
                    </a>
                    for assistance. You can unsubscribe at any time
                </span>
            </label>

            <button className={`btn btn-secondary w-full text-base-100 mt-4 ${isLoading ? 'btn-disabled' : ''}`} type='submit' disabled={isLoading}>
                {isLoading ? <span className="loading loading-spinner loading-lg text-secondary"></span> : 'SUBMIT'}
            </button>


            {/* T&C and Privacy Policy */}
            <div className='flex justify-center underline text-blue-600 text-xs'>
                <NavLink className='px-2' to='/terms-and-condition' target='_blank'>Terms and Conditions</NavLink>
                <NavLink className='px-2' to='/privacy-policy' target='_blank'>Privacy Policy</NavLink>
            </div>
        </form>
    );
};

export default AgentForm;
